import React, { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Switch } from "@/components/ui-kit";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";

export const PageTitle = ({ title, sub, action }: { title: string; sub?: string; action?: ReactNode }) => (
  <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
    <div>
      <h1 data-testid="page-title" className="font-display text-2xl sm:text-3xl font-black tracking-tight">{title}</h1>
      {sub && <p className="text-muted-foreground mt-1 text-sm sm:text-base">{sub}</p>}
    </div>
    {action}
  </div>
);

export const SettingsPanel = ({ children }: { children?: ReactNode }) => {
  const { t, lang, theme, toggleLang, toggleTheme } = usePrefs();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const row = "flex items-center justify-between gap-4 p-4";
  return (
    <div className="space-y-4 max-w-xl">
      {user && (
        <div className="rounded-3xl border bg-card p-5 flex items-center gap-4" data-testid="account-card">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-14 w-14 rounded-2xl object-cover" />
          ) : (
            <div className="h-14 w-14 rounded-2xl bg-primary text-primary-foreground grid place-items-center font-display text-2xl font-black">
              {user.name?.[0]}
            </div>
          )}
          <div className="min-w-0">
            <p data-testid="account-name" className="font-bold text-lg truncate">{user.name}</p>
            <p data-testid="account-email" className="text-sm text-muted-foreground truncate" dir="ltr">{user.email || (user.username ? `@${user.username}` : "")}</p>
            <span data-testid="account-role" className="inline-block mt-1 rounded-full bg-accent px-2.5 py-0.5 text-xs font-bold">
              {t(`role.${user.role}`)}{user.venue_role || user.admin_role ? ` · ${t(`role.${user.venue_role || user.admin_role}`)}` : ""}
            </span>
          </div>
        </div>
      )}
      {children}
      <div className="rounded-3xl border bg-card divide-y">
        <div className={row}>
          <span className="font-semibold">{t("settings.language")}</span>
          <button data-testid="settings-lang-button" onClick={toggleLang} className="h-10 px-4 rounded-full bg-accent font-bold text-sm">
            {lang === "ar" ? "العربية" : "English"} ⇄
          </button>
        </div>
        <div className={row}>
          <span className="font-semibold">{t("settings.theme")}</span>
          <Switch data-testid="settings-theme-switch" checked={theme === "dark"} onCheckedChange={toggleTheme} />
        </div>
      </div>
      <button
        data-testid="logout-button"
        onClick={async () => {
          await logout();
          navigate("/");
        }}
        className="w-full h-12 rounded-full border-2 border-primary/30 text-primary font-bold inline-flex items-center justify-center gap-2 hover:bg-primary/10 transition-[background-color]"
      >
        <LogOut className="h-4 w-4 rtl:rotate-180" />
        {t("auth.logout")}
      </button>
    </div>
  );
};
